import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import { getAuthToken } from "../utils/session";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SortIcon from "@mui/icons-material/Sort";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

const FONT_FAMILY =
  'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

const getDashboardTheme = (mode) => {
  const isDark = mode === "dark";

  return {
    mode: isDark ? "dark" : "light",

    page: isDark ? "#07111F" : "#F7F9FC",
    pageSoft: isDark ? "#0B1728" : "#FFFFFF",
    surface: isDark ? "rgba(255,255,255,0.065)" : "#FFFFFF",
    surfaceStrong: isDark ? "rgba(255,255,255,0.095)" : "#FBFCFE",
    input: isDark ? "rgba(255,255,255,0.055)" : "#FFFFFF",

    line: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,36,56,0.10)",
    lineStrong: isDark ? "rgba(148,163,184,0.24)" : "rgba(15,36,56,0.16)",

    text: isDark ? "#F8FAFC" : "#172033",
    textSoft: isDark ? "#D8E3F0" : "#334155",
    muted: isDark ? "#91A4BC" : "#64748B",

    primary: isDark ? "#4F9CF9" : "#1E4B81",
    primaryDark: isDark ? "#DCEEFF" : "#102840",
    primarySoft: isDark ? "rgba(79,156,249,0.16)" : "#EEF5FF",
    primaryLine: isDark ? "rgba(79,156,249,0.34)" : "rgba(30,75,129,0.28)",

    accent: "#D37D36",
    accentDark: "#B6632C",
    accentSoft: isDark ? "rgba(211,125,54,0.16)" : "#FFF4EA",

    success: isDark ? "#22C55E" : "#14A67A",
    successSoft: isDark ? "rgba(34,197,94,0.14)" : "#EAFBF6",

    warning: "#D37D36",
    warningSoft: isDark ? "rgba(211,125,54,0.16)" : "#FFF4EA",

    danger: isDark ? "#FB7185" : "#D32F2F",
    dangerSoft: isDark ? "rgba(251,113,133,0.14)" : "#FFEBEE",

    purple: isDark ? "#A78BFA" : "#5B5CE2",
    purpleSoft: isDark ? "rgba(167,139,250,0.16)" : "#F0F0FF",

    cardShadow: isDark
      ? "0 24px 60px rgba(0,0,0,0.24)"
      : "0 18px 48px rgba(15,36,56,0.06)",

    hoverShadow: isDark
      ? "0 18px 42px rgba(0,0,0,0.24)"
      : "0 18px 42px rgba(15,36,56,0.08)",

    pageBackground: isDark
      ? "radial-gradient(circle at 18% 0%, rgba(79,156,249,0.16), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.10), transparent 28%), linear-gradient(180deg, #07111F 0%, #0B1728 48%, #07111F 100%)"
      : "radial-gradient(circle at 18% 0%, rgba(30,75,129,0.08), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.08), transparent 28%), linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 48%, #F7F9FC 100%)",
  };
};

const getCardBaseSx = (C) => ({
  bgcolor: C.surface,
  border: `1px solid ${C.line}`,
  borderRadius: "22px",
  boxShadow: C.cardShadow,
  backdropFilter: C.mode === "dark" ? "blur(18px)" : "none",
});

const getControlSx = (C) => ({
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "14px",
    bgcolor: C.input,
    color: C.text,
    fontWeight: 600,
    fontSize: "0.76rem",
    fontFamily: FONT_FAMILY,

    "& fieldset": {
      borderColor: C.line,
    },

    "&:hover fieldset": {
      borderColor: C.primaryLine,
    },

    "&.Mui-focused fieldset": {
      borderColor: C.primary,
    },

    "& input::placeholder": {
      color: C.muted,
      opacity: 1,
    },
  },
});

const getSelectSx = (C) => ({
  height: 42,
  borderRadius: "14px",
  bgcolor: C.input,
  color: C.textSoft,
  fontWeight: 700,
  fontSize: "0.74rem",
  fontFamily: FONT_FAMILY,

  "& fieldset": {
    borderColor: C.line,
  },

  "&:hover fieldset": {
    borderColor: C.primaryLine,
  },

  "&.Mui-focused fieldset": {
    borderColor: C.primary,
  },

  "& .MuiSvgIcon-root": {
    color: C.muted,
  },
});

const getMenuProps = (C) => ({
  PaperProps: {
    sx: {
      mt: 0.8,
      bgcolor: C.pageSoft,
      color: C.text,
      border: `1px solid ${C.line}`,
      boxShadow:
        C.mode === "dark"
          ? "0 22px 54px rgba(0,0,0,0.34)"
          : "0 18px 44px rgba(15,36,56,0.12)",
      borderRadius: "14px",
      fontFamily: FONT_FAMILY,

      "& .MuiMenuItem-root": {
        fontSize: "0.76rem",
        fontWeight: 600,
        fontFamily: FONT_FAMILY,
      },
    },
  },
});

const getPlanInfo = (role, C) => {
  const normalizedRole = String(role || "").toUpperCase();

  if (normalizedRole.includes("FREEMIUM")) {
    return {
      label: "Freemium",
      maxEvaluations: 1,
      processes: "5 procesos",
      maturity: "Sin madurez organizacional",
      color: C.primary,
      bg: C.primarySoft,
    };
  }

  if (normalizedRole.includes("PREMIUM_PRO") || normalizedRole.includes("PREMIUMPRO")) {
    return {
      label: "Premium Pro",
      maxEvaluations: Infinity,
      processes: "22 procesos",
      maturity: "Madurez organizacional nivel 5",
      color: C.accent,
      bg: C.accentSoft,
    };
  }

  if (normalizedRole.includes("PREMIUM")) {
    return {
      label: "Premium",
      maxEvaluations: 2,
      processes: "9 procesos",
      maturity: "Madurez organizacional nivel 2",
      color: C.purple,
      bg: C.purpleSoft,
    };
  }

  return {
    label: "Plan",
    maxEvaluations: Infinity,
    processes: "Según configuración",
    maturity: "Según configuración",
    color: C.primary,
    bg: C.primarySoft,
  };
};

const formatDate = (date) => {
  if (!date) return "Sin fecha";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "Sin fecha";

  return parsed.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getEvaluationTitle = (evaluation) =>
  evaluation?.cuestionario?.titulo || "Autoevaluación";

const getProgress = (evaluation) => {
  const progress = Number(evaluation?.progreso || 0);
  return Math.max(0, Math.min(progress, 100));
};

const getStatusConfig = (isCompleted, C) => {
  if (isCompleted) {
    return {
      label: "Completada",
      color: C.success,
      bg: C.successSoft,
      icon: <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} />,
    };
  }

  return {
    label: "En curso",
    color: C.warning,
    bg: C.warningSoft,
    icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 14 }} />,
  };
};

const DashboardSkeleton = ({ C }) => (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: C.page,
      py: { xs: 3, md: 4 },
      fontFamily: FONT_FAMILY,
    }}
  >
    <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1.25, sm: 1.75, md: 2 } }}>
      <Skeleton variant="text" width={320} height={44} />
      <Skeleton variant="text" width="45%" height={22} sx={{ mb: 3 }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {[...Array(4)].map((_, index) => (
          <Paper key={index} elevation={0} sx={{ ...getCardBaseSx(C), p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 3 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="38%" height={32} />
              </Box>
            </Stack>
          </Paper>
        ))}
      </Box>

      <Skeleton variant="rounded" height={420} sx={{ borderRadius: 5 }} />
    </Container>
  </Box>
);

const SectionHeader = ({ title, subtitle, C }) => (
  <Box sx={{ mb: 1.8 }}>
    <Typography
      sx={{
        color: C.text,
        fontWeight: 850,
        fontSize: "0.98rem",
        letterSpacing: "-0.025em",
        fontFamily: FONT_FAMILY,
      }}
    >
      {title}
    </Typography>

    {subtitle && (
      <Typography
        sx={{
          color: C.muted,
          fontWeight: 560,
          fontSize: "0.74rem",
          mt: 0.35,
          fontFamily: FONT_FAMILY,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const MetricCard = ({ title, value, helper, icon, color, bg, C }) => (
  <Paper
    elevation={0}
    sx={{
      ...getCardBaseSx(C),
      p: { xs: 1.7, md: 1.9 },
      height: "100%",
      transition: "all 200ms ease",

      "&:hover": {
        transform: { xs: "none", md: "translateY(-3px)" },
        borderColor: C.primaryLine,
        boxShadow: C.hoverShadow,
      },
    }}
  >
    <Stack direction="row" spacing={1.35} alignItems="center">
      <Box
        sx={{
          width: 46,
          height: 46,
          borderRadius: "15px",
          display: "grid",
          placeItems: "center",
          color,
          bgcolor: bg,
          flexShrink: 0,
          border: `1px solid ${C.line}`,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: C.muted,
            fontWeight: 700,
            fontSize: "0.68rem",
            mb: 0.4,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: C.text,
            fontWeight: 900,
            fontSize: { xs: "1.45rem", md: "1.65rem" },
            letterSpacing: "-0.05em",
            lineHeight: 1,
            fontFamily: FONT_FAMILY,
          }}
        >
          {value}
        </Typography>

        {helper && (
          <Typography
            sx={{
              color: C.muted,
              fontWeight: 520,
              fontSize: "0.68rem",
              mt: 0.55,
              fontFamily: FONT_FAMILY,
            }}
          >
            {helper}
          </Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const PlanSummaryCard = ({
  planInfo,
  evaluationsCount,
  canCreateEvaluation,
  onCreate,
  C,
}) => {
  const limitText =
    planInfo.maxEvaluations === Infinity
      ? "Ilimitadas"
      : `${evaluationsCount}/${planInfo.maxEvaluations}`;

  const progress =
    planInfo.maxEvaluations === Infinity
      ? 100
      : Math.min((evaluationsCount / planInfo.maxEvaluations) * 100, 100);

  return (
    <Paper elevation={0} sx={{ ...getCardBaseSx(C), p: 2 }}>
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.8 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "15px",
            display: "grid",
            placeItems: "center",
            color: planInfo.color,
            bgcolor: planInfo.bg,
            border: `1px solid ${C.line}`,
          }}
        >
          <WorkspacePremiumIcon sx={{ fontSize: 21 }} />
        </Box>

        <Box>
          <Typography
            sx={{
              color: C.text,
              fontWeight: 850,
              fontSize: "0.86rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            Plan actual
          </Typography>

          <Typography
            sx={{
              color: planInfo.color,
              fontWeight: 850,
              fontSize: "0.76rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            {planInfo.label}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mb: 1.8 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.7 }}>
          <Typography
            sx={{
              color: C.muted,
              fontWeight: 700,
              fontSize: "0.72rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            Autoevaluaciones
          </Typography>

          <Typography
            sx={{
              color: C.text,
              fontWeight: 800,
              fontSize: "0.72rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            {limitText}
          </Typography>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 7,
            borderRadius: 99,
            bgcolor: C.mode === "dark" ? "rgba(255,255,255,0.075)" : "rgba(15,36,56,0.08)",

            "& .MuiLinearProgress-bar": {
              borderRadius: 99,
              bgcolor: planInfo.color,
            },
          }}
        />
      </Box>

      <Stack spacing={0.9} sx={{ mb: 1.9 }}>
        <Stack direction="row" spacing={0.85} alignItems="center">
          <TaskAltIcon sx={{ color: C.accent, fontSize: 16 }} />
          <Typography
            sx={{
              color: C.textSoft,
              fontSize: "0.74rem",
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
            }}
          >
            {planInfo.processes}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.85} alignItems="center">
          <ShieldOutlinedIcon sx={{ color: C.primary, fontSize: 16 }} />
          <Typography
            sx={{
              color: C.textSoft,
              fontSize: "0.74rem",
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
            }}
          >
            {planInfo.maturity}
          </Typography>
        </Stack>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
        onClick={onCreate}
        disabled={!canCreateEvaluation}
        sx={{
          borderRadius: "13px",
          py: 1,
          textTransform: "none",
          fontWeight: 800,
          fontSize: "0.76rem",
          fontFamily: FONT_FAMILY,
          bgcolor: C.primary,
          boxShadow:
            C.mode === "dark"
              ? "0 14px 28px rgba(79,156,249,0.18)"
              : "0 14px 28px rgba(30,75,129,0.14)",

          "&:hover": {
            bgcolor: C.mode === "dark" ? "#3E8BE5" : C.primaryDark,
          },

          "&.Mui-disabled": {
            bgcolor: "rgba(148,163,184,0.16)",
            color: C.muted,
          },
        }}
      >
        Nueva autoevaluación
      </Button>

      {!canCreateEvaluation && (
        <Typography
          sx={{
            mt: 1.2,
            color: C.warning,
            fontWeight: 600,
            fontSize: "0.68rem",
            lineHeight: 1.45,
            fontFamily: FONT_FAMILY,
          }}
        >
          Has alcanzado el límite de tu plan. Para ampliarlo, contacta con dqpa@dqteam.es.
        </Typography>
      )}
    </Paper>
  );
};

const EvaluationCard = ({ evaluation, navigate, C }) => {
  const progress = getProgress(evaluation);
  const isCompleted = progress === 100 || Boolean(evaluation.estado);
  const status = getStatusConfig(isCompleted, C);
  const title = getEvaluationTitle(evaluation);
  const questionnaireId = evaluation?.cuestionario?._id;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.45, md: 1.55 },
        borderRadius: "18px",
        border: `1px solid ${C.line}`,
        bgcolor: C.mode === "dark" ? "rgba(255,255,255,0.045)" : C.surface,
        transition: "all 200ms ease",

        "&:hover": {
          transform: { xs: "none", md: "translateY(-2px)" },
          borderColor: C.primaryLine,
          boxShadow: C.hoverShadow,
        },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.7}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "14px",
              display: "grid",
              placeItems: "center",
              color: status.color,
              bgcolor: status.bg,
              flexShrink: 0,
              border: `1px solid ${C.line}`,
            }}
          >
            {isCompleted ? (
              <CheckCircleOutlinedIcon sx={{ fontSize: 22 }} />
            ) : (
              <HourglassEmptyOutlinedIcon sx={{ fontSize: 22 }} />
            )}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={0.9}
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ mb: 0.75 }}
            >
              <Typography
                sx={{
                  color: C.text,
                  fontWeight: 800,
                  fontSize: "0.86rem",
                  lineHeight: 1.25,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {title}
              </Typography>

              <Chip
                size="small"
                icon={status.icon}
                label={status.label}
                sx={{
                  height: 23,
                  bgcolor: status.bg,
                  color: status.color,
                  fontWeight: 800,
                  fontSize: "0.66rem",
                  border: `1px solid ${C.line}`,
                  fontFamily: FONT_FAMILY,

                  "& .MuiChip-icon": {
                    color: status.color,
                  },
                }}
              />
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.65, sm: 1.7 }}
              sx={{ color: C.muted, mb: 1 }}
            >
              <Stack direction="row" spacing={0.6} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 14 }} />
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {formatDate(evaluation.fecha)}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={0.6} alignItems="center">
                <BarChartIcon sx={{ fontSize: 14 }} />
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {progress}% completado
                </Typography>
              </Stack>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 7,
                borderRadius: 99,
                bgcolor: C.mode === "dark" ? "rgba(255,255,255,0.075)" : "rgba(15,36,56,0.08)",
                maxWidth: { xs: "100%", md: 520 },

                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  bgcolor: isCompleted ? C.success : C.accent,
                },
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.85}
          justifyContent="flex-end"
          sx={{ flexShrink: 0 }}
        >
          {isCompleted ? (
            <>
              <Button
                variant="outlined"
                size="small"
                disabled={!questionnaireId}
                onClick={() => navigate(`/cuestionario/${questionnaireId}?completed=true`)}
                sx={{
                  minWidth: 104,
                  height: 36,
                  borderColor: C.lineStrong,
                  color: C.textSoft,
                  fontWeight: 750,
                  fontSize: "0.72rem",
                  textTransform: "none",
                  borderRadius: "12px",
                  fontFamily: FONT_FAMILY,

                  "&:hover": {
                    borderColor: C.primary,
                    bgcolor: C.primarySoft,
                  },
                }}
              >
                Ver respuestas
              </Button>

              <Button
                variant="contained"
                size="small"
                disabled={!questionnaireId}
                onClick={() => navigate(`/resultados/${questionnaireId}`)}
                sx={{
                  minWidth: 104,
                  height: 36,
                  bgcolor: C.primary,
                  color: "#FFFFFF",
                  fontWeight: 800,
                  fontSize: "0.72rem",
                  textTransform: "none",
                  borderRadius: "12px",
                  boxShadow: "none",
                  fontFamily: FONT_FAMILY,

                  "&:hover": {
                    bgcolor: C.mode === "dark" ? "#3E8BE5" : C.primaryDark,
                  },
                }}
              >
                Resultados
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 17 }} />}
              disabled={!questionnaireId}
              onClick={() => navigate(`/cuestionario/${questionnaireId}`)}
              sx={{
                minWidth: 142,
                height: 36,
                bgcolor: C.accent,
                color: "#FFFFFF",
                fontWeight: 800,
                fontSize: "0.72rem",
                textTransform: "none",
                borderRadius: "12px",
                boxShadow: "none",
                fontFamily: FONT_FAMILY,

                "&:hover": {
                  bgcolor: C.accentDark,
                },
              }}
            >
              Continuar
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

const EmptyState = ({ onCreate, canCreateEvaluation, C }) => (
  <Paper
    elevation={0}
    sx={{
      minHeight: 310,
      borderRadius: "22px",
      border: `1px dashed ${C.lineStrong}`,
      bgcolor: C.surfaceStrong,
      p: { xs: 3, md: 4 },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
    }}
  >
    <Box sx={{ maxWidth: 560 }}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "21px",
          display: "grid",
          placeItems: "center",
          mx: "auto",
          mb: 1.8,
          color: C.accent,
          bgcolor: C.accentSoft,
          border: `1px solid ${C.line}`,
        }}
      >
        <InsertChartOutlinedIcon sx={{ fontSize: 31 }} />
      </Box>

      <Typography
        sx={{
          color: C.text,
          fontWeight: 900,
          fontSize: { xs: "1.2rem", md: "1.45rem" },
          letterSpacing: "-0.04em",
          mb: 0.9,
          fontFamily: FONT_FAMILY,
        }}
      >
        Aún no tienes autoevaluaciones
      </Typography>

      <Typography
        sx={{
          color: C.muted,
          lineHeight: 1.7,
          mb: 2.5,
          fontSize: "0.82rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        Crea tu primera autoevaluación para conocer el estado de tus procesos de datos y obtener recomendaciones de mejora.
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddCircleOutlineIcon />}
        onClick={onCreate}
        disabled={!canCreateEvaluation}
        sx={{
          borderRadius: "14px",
          px: 2.5,
          py: 1,
          textTransform: "none",
          fontWeight: 850,
          fontSize: "0.76rem",
          bgcolor: C.accent,
          boxShadow: "0 14px 30px rgba(211,125,54,0.18)",
          fontFamily: FONT_FAMILY,

          "&:hover": {
            bgcolor: C.accentDark,
          },
        }}
      >
        Crear autoevaluación
      </Button>
    </Box>
  </Paper>
);

const NoResults = ({ C }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3.5,
      borderRadius: "20px",
      bgcolor: C.surfaceStrong,
      border: `1px dashed ${C.lineStrong}`,
      textAlign: "center",
    }}
  >
    <ErrorOutlineIcon sx={{ color: C.accent, fontSize: 36, mb: 1 }} />

    <Typography
      sx={{
        color: C.text,
        fontWeight: 850,
        mb: 0.55,
        fontSize: "0.9rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      No hay resultados
    </Typography>

    <Typography
      sx={{
        color: C.muted,
        fontSize: "0.78rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      Cambia la búsqueda, el estado o el periodo seleccionado.
    </Typography>
  </Paper>
);

const Dashboard = () => {
  const navigate = useNavigate();

  const themeMode = "light";
  const C = useMemo(() => getDashboardTheme("light"), []);

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sortOrder, setSortOrder] = useState("recientes");
  const [statusFilter, setStatusFilter] = useState("todas");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [userRole, setUserRole] = useState("");
  const [showUpgradeAlert, setShowUpgradeAlert] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(true);

  const planInfo = useMemo(() => getPlanInfo(userRole, C), [userRole, C]);

  const completedCount = useMemo(
    () =>
      evaluations.filter(
        (evaluation) => getProgress(evaluation) === 100 || evaluation.estado
      ).length,
    [evaluations]
  );

  const pendingCount = evaluations.length - completedCount;

  const averageProgress = useMemo(() => {
    if (!evaluations.length) return 0;

    const total = evaluations.reduce(
      (sum, evaluation) => sum + getProgress(evaluation),
      0
    );

    return Math.round(total / evaluations.length);
  }, [evaluations]);

  const canCreateEvaluation =
    planInfo.maxEvaluations === Infinity ||
    evaluations.length < planInfo.maxEvaluations;

  const filteredEvaluations = useMemo(() => {
    let result = [...evaluations];

    if (statusFilter === "completadas") {
      result = result.filter(
        (evaluation) => getProgress(evaluation) === 100 || evaluation.estado
      );
    }

    if (statusFilter === "pendientes") {
      result = result.filter(
        (evaluation) => getProgress(evaluation) < 100 && !evaluation.estado
      );
    }

    if (dateRangeFilter !== "all") {
      const days = dateRangeFilter === "7d" ? 7 : 30;
      const cutoff = dayjs().subtract(days, "day");

      result = result.filter((evaluation) =>
        dayjs(evaluation.fecha).isAfter(cutoff)
      );
    }

    if (searchQuery.trim()) {
      const normalizedSearch = searchQuery.trim().toLowerCase();

      result = result.filter((evaluation) =>
        getEvaluationTitle(evaluation).toLowerCase().includes(normalizedSearch)
      );
    }

    result.sort((a, b) => {
      const dateA = new Date(a.fecha || 0);
      const dateB = new Date(b.fecha || 0);

      return sortOrder === "recientes" ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [evaluations, statusFilter, dateRangeFilter, searchQuery, sortOrder]);

  useEffect(() => {
    localStorage.setItem("dashboardThemeMode", "light");
    document.body.style.background = C.page;
  }, [C.page]);

  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        const token = getAuthToken();
        if (!token) throw new Error("No se ha encontrado la sesión del usuario.");

        const decodedToken = jwtDecode(token);
        const userId = decodedToken.id;
        const role = decodedToken.role || "";

        setUserRole(role);

        const response = await axiosInstance.get(
          API_ENDPOINTS.resumenAutoevaluaciones(userId),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const evaluationsWithState = (
          response.data?.autoevaluaciones || []
        ).map((evaluation) => ({
          ...evaluation,
          estado: getProgress(evaluation) === 100,
        }));

        setEvaluations(evaluationsWithState);

        const currentPlan = getPlanInfo(role, C);
        const hasReachedLimit =
          currentPlan.maxEvaluations !== Infinity &&
          evaluationsWithState.length >= currentPlan.maxEvaluations;

        if (hasReachedLimit) {
          setShowUpgradeAlert(true);
          setIsAlertVisible(true);
        }
      } catch (err) {
        setError(
          "No hemos podido cargar tus autoevaluaciones. Inténtalo de nuevo más tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, []);

  const handleCreateEvaluation = () => {
    if (!canCreateEvaluation) {
      setShowUpgradeAlert(true);
      setIsAlertVisible(true);
      return;
    }

    navigate("/nueva-autoevaluacion");
  };


  if (loading) return <DashboardSkeleton C={C} />;

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: C.page,
          display: "grid",
          placeItems: "center",
          p: 3,
          fontFamily: FONT_FAMILY,
        }}
      >
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            maxWidth: 560,
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: { xs: 8.5, md: 3.5 },
        pb: { xs: 4, md: 5 },
        fontFamily: FONT_FAMILY,
        color: C.text,
        background: C.pageBackground,
        transition: "background 220ms ease, color 220ms ease",
      }}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1.25, sm: 1.75, md: 2 } }}>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
            sx={{ mb: 2.6 }}
          >
            <Box>
              <Chip
                label="Panel de control"
                size="small"
                sx={{
                  mb: 1.1,
                  height: 24,
                  bgcolor: C.primarySoft,
                  color: C.primary,
                  border: `1px solid ${C.primaryLine}`,
                  fontWeight: 800,
                  fontSize: "0.66rem",
                  fontFamily: FONT_FAMILY,
                }}
              />

              <Typography
                sx={{
                  color: C.text,
                  fontWeight: 900,
                  fontSize: { xs: "1.6rem", md: "2.05rem" },
                  letterSpacing: "-0.055em",
                  lineHeight: 1.05,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Mis autoevaluaciones
              </Typography>

              <Typography
                sx={{
                  color: C.muted,
                  mt: 0.75,
                  fontWeight: 500,
                  fontSize: { xs: "0.82rem", md: "0.88rem" },
                  lineHeight: 1.55,
                  maxWidth: 760,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Revisa tu avance, continúa evaluaciones pendientes y consulta tus resultados estratégicos.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                onClick={handleCreateEvaluation}
                disabled={!canCreateEvaluation}
                sx={{
                  borderRadius: "14px",
                  px: 2.2,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 850,
                  fontSize: "0.76rem",
                  bgcolor: C.primary,
                  boxShadow:
                    C.mode === "dark"
                      ? "0 14px 32px rgba(79,156,249,0.22)"
                      : "0 14px 32px rgba(30,75,129,0.16)",
                  fontFamily: FONT_FAMILY,

                  "&:hover": {
                    bgcolor: C.mode === "dark" ? "#3E8BE5" : C.primaryDark,
                  },
                }}
              >
                Nueva autoevaluación
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.8,
              mb: 2.3,
            }}
          >
            <MetricCard
              C={C}
              title="Total"
              value={evaluations.length}
              helper="Autoevaluaciones creadas"
              icon={<InsertChartOutlinedIcon sx={{ fontSize: 24 }} />}
              color={C.primary}
              bg={C.primarySoft}
            />

            <MetricCard
              C={C}
              title="Completadas"
              value={completedCount}
              helper="Finalizadas al 100%"
              icon={<CheckCircleOutlinedIcon sx={{ fontSize: 24 }} />}
              color={C.success}
              bg={C.successSoft}
            />

            <MetricCard
              C={C}
              title="En curso"
              value={pendingCount}
              helper="Pendientes de terminar"
              icon={<HourglassEmptyOutlinedIcon sx={{ fontSize: 24 }} />}
              color={C.accent}
              bg={C.accentSoft}
            />

            <MetricCard
              C={C}
              title="Progreso medio"
              value={`${averageProgress}%`}
              helper="Avance global"
              icon={<TrendingUpIcon sx={{ fontSize: 24 }} />}
              color={C.purple}
              bg={C.purpleSoft}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 1fr) 330px",
              },
              gap: 2.2,
              alignItems: "start",
            }}
          >
            <Stack spacing={2.2} sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  ...getCardBaseSx(C),
                  p: { xs: 1.25, md: 1.5 },
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.1,
                    alignItems: "center",
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <TextField
                    size="small"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Buscar por nombre..."
                    sx={{
                      ...getControlSx(C),
                      flex: { xs: "1 1 100%", md: "1 1 240px" },
                      minWidth: { xs: "100%", md: 220 },
                      maxWidth: { xs: "100%", lg: 420 },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: C.muted, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl
                    size="small"
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 145px",
                        lg: "0 1 145px",
                      },
                      minWidth: 0,
                    }}
                  >
                    <Select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      startAdornment={
                        <FilterListIcon sx={{ mr: 0.8, color: C.muted, fontSize: 17 }} />
                      }
                      sx={getSelectSx(C)}
                      MenuProps={getMenuProps(C)}
                    >
                      <MenuItem value="todas">Todas</MenuItem>
                      <MenuItem value="completadas">Completadas</MenuItem>
                      <MenuItem value="pendientes">En curso</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 160px",
                        lg: "0 1 160px",
                      },
                      minWidth: 0,
                    }}
                  >
                    <Select
                      value={sortOrder}
                      onChange={(event) => setSortOrder(event.target.value)}
                      startAdornment={
                        <SortIcon sx={{ mr: 0.8, color: C.muted, fontSize: 17 }} />
                      }
                      sx={getSelectSx(C)}
                      MenuProps={getMenuProps(C)}
                    >
                      <MenuItem value="recientes">Más recientes</MenuItem>
                      <MenuItem value="antiguas">Más antiguas</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 165px",
                        lg: "0 1 165px",
                      },
                      minWidth: 0,
                    }}
                  >
                    <Select
                      value={dateRangeFilter}
                      onChange={(event) => setDateRangeFilter(event.target.value)}
                      sx={getSelectSx(C)}
                      MenuProps={getMenuProps(C)}
                    >
                      <MenuItem value="all">Todas las fechas</MenuItem>
                      <MenuItem value="7d">Últimos 7 días</MenuItem>
                      <MenuItem value="30d">Últimos 30 días</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  ...getCardBaseSx(C),
                  p: { xs: 1.45, md: 1.75 },
                  minWidth: 0,
                }}
              >
                <SectionHeader
                  C={C}
                  title="Autoevaluaciones"
                  subtitle={`${filteredEvaluations.length} resultado${
                    filteredEvaluations.length !== 1 ? "s" : ""
                  } encontrado${filteredEvaluations.length !== 1 ? "s" : ""}`}
                />

                {evaluations.length === 0 ? (
                  <EmptyState
                    C={C}
                    onCreate={handleCreateEvaluation}
                    canCreateEvaluation={canCreateEvaluation}
                  />
                ) : filteredEvaluations.length === 0 ? (
                  <NoResults C={C} />
                ) : (
                  <Stack spacing={1.25}>
                    {filteredEvaluations.map((evaluation) => (
                      <EvaluationCard
                        C={C}
                        key={evaluation._id}
                        evaluation={evaluation}
                        navigate={navigate}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>

            <Stack spacing={2.2} sx={{ minWidth: 0 }}>
              <PlanSummaryCard
                C={C}
                planInfo={planInfo}
                evaluationsCount={evaluations.length}
                canCreateEvaluation={canCreateEvaluation}
                onCreate={handleCreateEvaluation}
              />

              <Paper elevation={0} sx={{ ...getCardBaseSx(C), p: 2 }}>
                <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.8 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "14px",
                      display: "grid",
                      placeItems: "center",
                      color: C.primary,
                      bgcolor: C.primarySoft,
                      border: `1px solid ${C.line}`,
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: 21 }} />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: C.text,
                        fontWeight: 850,
                        fontSize: "0.86rem",
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Resumen
                    </Typography>

                    <Typography
                      sx={{
                        color: C.muted,
                        fontWeight: 560,
                        fontSize: "0.68rem",
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Estado de tus evaluaciones
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={1.35}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.7 }}>
                      <Typography
                        sx={{
                          color: C.muted,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        Completadas
                      </Typography>

                      <Typography
                        sx={{
                          color: C.success,
                          fontWeight: 850,
                          fontSize: "0.72rem",
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        {completedCount}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={evaluations.length ? (completedCount / evaluations.length) * 100 : 0}
                      sx={{
                        height: 7,
                        borderRadius: 99,
                        bgcolor: C.mode === "dark" ? "rgba(255,255,255,0.075)" : "rgba(15,36,56,0.08)",

                        "& .MuiLinearProgress-bar": {
                          borderRadius: 99,
                          bgcolor: C.success,
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.7 }}>
                      <Typography
                        sx={{
                          color: C.muted,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        En curso
                      </Typography>

                      <Typography
                        sx={{
                          color: C.accent,
                          fontWeight: 850,
                          fontSize: "0.72rem",
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        {pendingCount}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={evaluations.length ? (pendingCount / evaluations.length) * 100 : 0}
                      sx={{
                        height: 7,
                        borderRadius: 99,
                        bgcolor: C.mode === "dark" ? "rgba(255,255,255,0.075)" : "rgba(15,36,56,0.08)",

                        "& .MuiLinearProgress-bar": {
                          borderRadius: 99,
                          bgcolor: C.accent,
                        },
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: C.line }} />

                  <Typography
                    sx={{
                      color: C.textSoft,
                      fontSize: "0.76rem",
                      lineHeight: 1.65,
                      fontFamily: FONT_FAMILY,

                      "& strong": {
                        color: C.primary,
                        fontWeight: 850,
                      },
                    }}
                  >
                    Tu progreso medio actual es del <strong>{averageProgress}%</strong>. Continúa las evaluaciones pendientes para obtener resultados más completos.
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </motion.div>
      </Container>

      <Snackbar
        open={showUpgradeAlert && isAlertVisible}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="warning"
          icon={<WorkspacePremiumIcon />}
          action={
            <IconButton
              size="small"
              onClick={() => setIsAlertVisible(false)}
              aria-label="Cerrar aviso"
              sx={{ color: C.muted }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            maxWidth: 420,
            borderRadius: "18px",
            bgcolor: C.surface,
            border: `1px solid rgba(211,125,54,0.34)`,
            boxShadow: C.cardShadow,
            alignItems: "flex-start",
            color: C.text,
            fontFamily: FONT_FAMILY,

            "& .MuiAlert-icon": {
              color: C.accent,
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 850,
              color: C.text,
              mb: 0.35,
              fontSize: "0.82rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            Límite de autoevaluaciones alcanzado
          </Typography>

          <Typography
            sx={{
              fontSize: "0.72rem",
              color: C.muted,
              lineHeight: 1.45,
              fontFamily: FONT_FAMILY,
            }}
          >
            Para ampliar tu plan, contacta con{" "}
            <a
              href="mailto:dqpa@dqteam.es"
              style={{
                color: C.primary,
                fontWeight: 850,
                textDecoration: "none",
              }}
            >
              dqpa@dqteam.es
            </a>
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
